import React, { Suspense, useState } from 'react';
import { MotionConfig, motion } from 'framer-motion';
import {
  LuBell,
  LuBookOpen,
  LuBriefcase,
  LuCircleHelp,
  LuClipboardCheck,
  LuClipboardList,
  LuFileSpreadsheet,
  LuFileText,
  LuFolderOpen,
  LuGraduationCap,
  LuShieldAlert,
  LuUserRound,
} from 'react-icons/lu';
import PageHero from '../../components/data/PageHero';
import { fadeUp, staggerContainer } from '../../components/ui/motionPresets';
import { CARD, HelpNote, SectionHeader } from '../ProfilePage/components/ProfileUi';
import ModuleTile from './kit/ModuleTile';
import useQualityText from './kit/qualityText';
// Global legacy dark-mode rules on generic class names; kept so other screens look as before
import './QualityManagementDarkMode.css';

// Lazy load modals — code is only downloaded when the modal is first opened
const SOPModal = React.lazy(() => import('./SOPModal'));
const IndicationEducationModal = React.lazy(() => import('./IndicationEducationModal'));
const FormsModal = React.lazy(() => import('./FormsModal'));
const CapaModal = React.lazy(() => import('./CapaModal'));
const NotificationModal = React.lazy(() => import('./NotificationModal'));
const PolicyModal = React.lazy(() => import('./PolicyModal'));
const JobDescriptionModal = React.lazy(() => import('./JobDescriptionModal'));
const AssignmentViewModal = React.lazy(() => import('./AssignmentViewModal'));
const QuestionPoolModal = React.lazy(() => import('./QuestionPool/QuestionPoolModal'));

// `profile`: records are assigned to departments and show up in the employees' Profil › Dosyalar
const MODULES = {
  sop: { icon: LuFileText, gradient: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30', Dialog: SOPModal, profile: true },
  policy: { icon: LuBookOpen, gradient: 'tw-from-rose-400 tw-to-red-600 tw-shadow-rose-500/30', Dialog: PolicyModal, profile: true },
  jobDescription: { icon: LuBriefcase, gradient: 'tw-from-pink-400 tw-to-rose-600 tw-shadow-pink-500/30', Dialog: JobDescriptionModal, profile: true },
  forms: { icon: LuFileSpreadsheet, gradient: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30', Dialog: FormsModal, profile: true },
  education: { icon: LuGraduationCap, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30', Dialog: IndicationEducationModal, profile: true },
  questionPool: { icon: LuCircleHelp, gradient: 'tw-from-violet-400 tw-to-indigo-600 tw-shadow-violet-500/30', Dialog: QuestionPoolModal, quiz: true },
  assignments: { icon: LuClipboardList, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30', Dialog: AssignmentViewModal },
  capa: { icon: LuShieldAlert, gradient: 'tw-from-cyan-400 tw-to-sky-600 tw-shadow-cyan-500/30', Dialog: CapaModal },
  notification: { icon: LuBell, gradient: 'tw-from-emerald-400 tw-to-green-600 tw-shadow-emerald-500/30', Dialog: NotificationModal },
};

const SECTIONS = [
  { key: 'documents', icon: LuFolderOpen, gradient: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30', modules: ['sop', 'policy', 'jobDescription', 'forms'], grid: 'sm:tw-grid-cols-2 xl:tw-grid-cols-4' },
  { key: 'training', icon: LuGraduationCap, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30', modules: ['education', 'questionPool', 'assignments'], grid: 'sm:tw-grid-cols-2 lg:tw-grid-cols-3' },
  { key: 'records', icon: LuShieldAlert, gradient: 'tw-from-cyan-400 tw-to-sky-600 tw-shadow-cyan-500/30', modules: ['capa', 'notification'], grid: 'sm:tw-grid-cols-2' },
];

/**
 * Kalite Yönetimi: SOP, politika, görev tanımı, form ve endikasyon eğitimleri
 * (departmanlara atanır, çalışanların profilinde görünür), soru havuzu, atama
 * takibi, CAPA ve bildirimler. Her modül kendi penceresinde açılır.
 */
const QualityManagementPage = () => {
  const { label } = useQualityText();
  const [openModule, setOpenModule] = useState(null);
  const Dialog = openModule ? MODULES[openModule].Dialog : null;

  const badgesFor = (module) => {
    const badges = [];
    if (module.profile) badges.push({ key: 'profile', tone: 'accent', icon: LuUserRound, label: label('page.badges.profile') });
    if (module.quiz) badges.push({ key: 'quiz', tone: 'info', icon: LuCircleHelp, label: label('page.badges.quiz') });
    return badges;
  };

  const renderSection = (section) => (
    <section key={section.key} className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}>
      <SectionHeader
        icon={section.icon}
        gradient={section.gradient}
        title={label(`page.sections.${section.key}.title`)}
        description={label(`page.sections.${section.key}.text`)}
      />
      <div className={`tw-grid tw-gap-3 ${section.grid}`}>
        {section.modules.map((key) => (
          <ModuleTile
            key={key}
            icon={MODULES[key].icon}
            gradient={MODULES[key].gradient}
            title={label(`page.modules.${key}.title`)}
            text={label(`page.modules.${key}.text`)}
            badges={badgesFor(MODULES[key])}
            openLabel={label('page.open')}
            onOpen={() => setOpenModule(key)}
          />
        ))}
      </div>
    </section>
  );

  const [documents, training, records] = SECTIONS;

  return (
    <div className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-font-sans tw-antialiased md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
      <MotionConfig reducedMotion="user">
        <motion.div className="tw-space-y-4 sm:tw-space-y-5" variants={staggerContainer(0.06)} initial="hidden" animate="show">
          <motion.div variants={fadeUp}>
            <PageHero
              icon={LuClipboardCheck}
              gradient="tw-from-teal-400 tw-to-emerald-600 tw-shadow-teal-500/30"
              title={label('page.title')}
              subtitle={label('page.subtitle')}
            />
          </motion.div>

          {/* Buradan yüklenenlerin profile nasıl yansıdığı */}
          <motion.div variants={fadeUp}>
            <HelpNote>
              <p>{label('page.help.profile')}</p>
              <p className="tw-mt-1 tw-text-sky-800/80 dark:tw-text-sky-100/75">{label('page.help.care')}</p>
            </HelpNote>
          </motion.div>

          <motion.div variants={fadeUp}>{renderSection(documents)}</motion.div>

          <motion.div variants={fadeUp} className="tw-grid tw-gap-4 sm:tw-gap-5 2xl:tw-grid-cols-[3fr_2fr]">
            {renderSection(training)}
            {renderSection(records)}
          </motion.div>
        </motion.div>
      </MotionConfig>

      {/* Modals — only mounted when opened, code-split via React.lazy */}
      <Suspense fallback={null}>
        {Dialog && <Dialog isOpen onClose={() => setOpenModule(null)} />}
      </Suspense>
    </div>
  );
};

export default QualityManagementPage;
